// ***********************************************************************************************************//
// onclick swap button it will add the item and removed the current cart items
(() => {
  /**
   * CONFIG — tweak to match your theme
   * ----------------------------------
   * DRAWER_HANDLE: Shopify section handle for the cart drawer.
   * EXTRA_SECTIONS: any other sections you want to refresh (e.g. cart icon bubble).
   * DRAWER_SELECTORS: candidate selectors for the actual drawer element that owns the "open" state.
   * CONTENT_SELECTORS: candidate selectors for the inner, scrollable/content container we can safely replace.
   */
  const DRAWER_HANDLE = 'cart-drawer';
  const EXTRA_SECTIONS = ['cart-icon-bubble'];

  const DRAWER_SELECTORS = [
    'cart-drawer',
    '.cart-drawer',
    'details[data-drawer]',
    '[data-drawer]'
  ];

  const CONTENT_SELECTORS = [
    '[data-cart-items]',
    '.cart-drawer__inner',
    '.drawer__inner',
    '.cart-drawer__scroll',
    '[data-scroll-container]'
  ];

  /** -------------------------
   * Utilities
   * -------------------------- */

  function setGlobalLoader(on = true) {
    const el = document.querySelector('.uc-c-cart-loader');
    if (!el) return;
    if (on) {
      el.style.display = 'flex';
      el.setAttribute('aria-hidden', 'false');
      el.classList.add('is-visible');
    } else {
      el.style.display = 'none';
      el.setAttribute('aria-hidden', 'true');
      el.classList.remove('is-visible');
    }
  }

  

  function qsaWithin(root, selectors) {
    for (const sel of selectors) {
      const el = root && root.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function getDrawerSection() {
    return (
      document.getElementById('shopify-section-' + DRAWER_HANDLE) ||
      document.querySelector(`[data-section-id="${DRAWER_HANDLE}"]`)
    );
  }

  function getDrawerRoot(fromSection) {
    const host = fromSection || getDrawerSection();
    return qsaWithin(host || document, DRAWER_SELECTORS);
  }

  function getDrawerContent(fromDrawer) {
    const host = fromDrawer || getDrawerRoot();
    return qsaWithin(host || document, CONTENT_SELECTORS);
  }

  function isDrawerOpen(drawerEl) {
    if (!drawerEl) return false;
    return drawerEl.hasAttribute('open') || drawerEl.classList.contains('is-open');
  }

  function openDrawer(drawerEl) {
    if (!drawerEl) return;
    if (drawerEl.tagName.toLowerCase() === 'details') {
      drawerEl.setAttribute('open', '');
    } else {
      drawerEl.classList.add('is-open');
      // Some themes expose an imperative .open()
      if (typeof drawerEl.open === 'function') {
        try { drawerEl.open(); } catch { /* noop */ }
      }
    }
  }

  function setBusy(el, on = true) {
    if (!el) return;
    if (on) {
      el.setAttribute('aria-busy', 'true');
      el.classList.add('is-busy');
      el.style.pointerEvents = 'none';
    } else {
      el.removeAttribute('aria-busy');
      el.classList.remove('is-busy');
      el.style.pointerEvents = '';
    }
  }

  /** -------------------------
   * Shopify Cart API helpers
   * -------------------------- */

  async function addVariant(variantId, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: Number(qty) })
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || res.statusText);
    return json;
  }

  async function removeByKey(key) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    if (!res.ok) throw new Error('Failed to remove line by key');
  }

  async function removeAllOfVariant(variantId) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: { [variantId]: 0 } })
    });
    if (!res.ok) throw new Error('Failed to remove by variant id');
  }

  /** --------------------------------------------
   * Section refresh that PRESERVES drawer state
   * --------------------------------------------- */
  async function refreshDrawerPreservingState() {
    const handles = [DRAWER_HANDLE, ...EXTRA_SECTIONS].join(',');

    const section = getDrawerSection();
    const drawerEl = getDrawerRoot(section);
    const contentEl = getDrawerContent(drawerEl);

    // Snapshot visibility & UX state
    const wasOpen = isDrawerOpen(drawerEl);

    const bodyHadLock =
      document.documentElement.classList.contains('no-scroll') ||
      document.body.classList.contains('overflow-hidden');

    const prevScroll = contentEl ? contentEl.scrollTop : 0;
    const active = document.activeElement;

    // Fetch updated sections HTML
    const res = await fetch('/?sections=' + encodeURIComponent(handles), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    const htmls = await res.json();

    // Update drawer content ONLY (do not replace the drawer root)
    if (section && htmls[DRAWER_HANDLE]) {
      const tmp = document.createElement('div');
      tmp.innerHTML = htmls[DRAWER_HANDLE];

      const freshSection =
        tmp.querySelector('#shopify-section-' + DRAWER_HANDLE) ||
        tmp.querySelector(`[data-section-id="${DRAWER_HANDLE}"]`) ||
        tmp.firstElementChild;

      const freshDrawer = qsaWithin(freshSection, DRAWER_SELECTORS);
      const freshContent = qsaWithin(freshDrawer || freshSection, CONTENT_SELECTORS);

      if (drawerEl && contentEl && freshContent) {
        // Replace just the inner content node to keep component instance + listeners
        contentEl.replaceWith(freshContent);

        // Restore 'open' state and any body locks
        if (wasOpen) openDrawer(drawerEl);
        if (bodyHadLock) {
          document.documentElement.classList.add('no-scroll');
          document.body.classList.add('overflow-hidden');
        }

        // Restore scroll
        const newContentEl = getDrawerContent(drawerEl);
        if (newContentEl) newContentEl.scrollTop = prevScroll;

        // Try to restore focus (avoids blur-closing on some details drawers)
        if (active && typeof active.focus === 'function') {
          try { active.focus(); } catch { /* noop */ }
        }
      } else if (drawerEl && freshSection) {
        // Fallback: replace children of the drawer root (NOT the root itself)
        const freshDrawerMaybe = qsaWithin(freshSection, DRAWER_SELECTORS);
        const newInner = (freshDrawerMaybe || freshSection).innerHTML;
        drawerEl.innerHTML = newInner;

        if (wasOpen) openDrawer(drawerEl);
        const newContentEl = getDrawerContent(drawerEl);
        if (newContentEl) newContentEl.scrollTop = prevScroll;
      }
    }

    // Update any extra sections (icon bubble, etc.)
    for (const [handle, html] of Object.entries(htmls)) {
      if (handle === DRAWER_HANDLE) continue;
      const target =
        document.getElementById('shopify-section-' + handle) ||
        document.querySelector(`[data-section-id="${handle}"]`);
      if (target) target.innerHTML = html;
    }

    // Let theme scripts re-bind if they listen for this
    document.dispatchEvent(new CustomEvent('cart:refreshed'));
  }

  /** -------------------------
   * Click handler: swap variant
   * -------------------------- */
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('.cart_button_dt_swap,.cart_button_dt_swap_undo');
    if (!trigger) return;

    e.preventDefault();
    if (trigger.classList.contains('cart_button_dt_swap_undo')) {
      const undobtnn =true;
      sessionStorage.setItem('swap_undoBtn',undobtnn);
    }

    const newVariantId = trigger.dataset.productId || trigger.getAttribute('data-product-id');
    const parentVariantId = trigger.dataset.parentProductId || trigger.getAttribute('data-parent-product-id');
    const swapAddEventId = trigger.dataset.swapAddEventId || trigger.getAttribute('data-add-product-event-id');
    const swapUndoEventId = trigger.dataset.swapUndoEventId || trigger.getAttribute('data-undo-product-event-id');
    sessionStorage.setItem('swap_parentVariantId',parentVariantId);
    sessionStorage.setItem('swap_endoVariantId',newVariantId);
    if (!newVariantId) return;
    setGlobalLoader(true);
    setBusy(trigger, true);

    try {
      // 1) Add the new variant
      await addVariant(newVariantId, 1);

      // 2) Remove the original line
      const row = trigger.closest('[data-cart-item-key], [data-key], [data-line-key]');
      const lineKey =
        row?.getAttribute('data-cart-item-key') ||
        row?.getAttribute('data-key') ||
        row?.getAttribute('data-line-key');

      if (lineKey) {
        await removeByKey(lineKey);
        console.log("removedby")
      } else if (parentVariantId) {
        await removeAllOfVariant(parentVariantId);
        console.log("removed all")
      }

      // 3) Refresh the drawer WITHOUT toggling visibility
      await refreshDrawerPreservingState();
    } catch (err) {
      console.error('Swap failed:', err);
    } finally {
      setBusy(trigger, false);
      setGlobalLoader(false);
    }

    // tracking event of these buttons
    if (trigger.matches('.cart_button_dt_swap_undo')) {
    console.log('Clicked: UNDO button', {
      id: swapUndoEventId,
      swap_endoVariantId: trigger.dataset.productId,
      swap_parentVariantId: trigger.dataset.parentProductId
    });
  } else if (trigger.matches('.cart_button_dt_swap')) {
    console.log('Clicked: SWAP button', {
      id: swapAddEventId,
      swap_parentVariantId: trigger.dataset.productId,
      swap_endoVariantId: trigger.dataset.parentProductId
    });
  }
    
  });

  /** -------------------------------------------
   * Optional: expose manual refresh for debugging
   * -------------------------------------------- */
  window.__refreshCartDrawerPreservingState = refreshDrawerPreservingState;
})();
// onclick swap button it will add the item and removed the current cart items
// ***********************************************************************************************************//


// ***********************************************second logic*************************************************//


// ***********************************************************************************************************//
// new code for observing the cart event for checking swap item
// if swap product available than it will remove the swap suggestion item from cart 

(() => {
  // --- your flags/consts ---
  const KEY  = 'swap_endoVariantId';
  const KEYY = 'swap_parentVariantId';
  const KEYu = 'swap_undoBtn';

  const TARGETS  = '.test_cdw, #CartDrawer-Item-text_swaper_t, [id^="CartDrawer-Item-text_swaper_t"]';
  const TARGETSu = '.cart_button_dt_swap_undo,.swap_undo';
  const WATCHERS = ['#CartDrawer-CartItems','cart-drawer .drawer__inner','.drawer__contents','.cart-items'];

  const cfg  = { remove: false };
  const cfgu = { remove: false };

  // --- helpers (unchanged) ---
  const hasKey  = () => { try { return !!sessionStorage.getItem(KEY); }  catch { return false; } };
  const hasKeyu = () => { try { return !!sessionStorage.getItem(KEYu); } catch { return false; } };
  const extractNumericId = (str) => (String(str||'').match(/(\d+)\s*$/)||[])[1] || '';

  function ensureUndoBtn(container) {
    let btn = container.querySelector('.cart_button_dt_swap_undo.swap_undo');
    if (!btn) {
      btn = document.createElement('div');
      btn.className = 'cart_button_dt_swap_undo swap_undo';
      btn.innerHTML = 'undo<img src="https://cdn.shopify.com/s/files/1/0767/1110/0646/files/mynaui_undo-solid.svg?v=1757833338" width="12" height="12"/>';
      container.appendChild(btn);
    }
    return btn;
  }

  function appendSwapUndoIfMatch() {
    let picked = '';  try { picked = sessionStorage.getItem(KEY)  || ''; } catch {}
    let pickedD='';  try { pickedD= sessionStorage.getItem(KEYY) || ''; } catch {}
    if (!picked || !pickedD) return;

    const container = document.getElementById(`swap_undo_${picked}`);
    if (!container) return;

    const thisItemVariantId = extractNumericId(container.id);
    const btn = ensureUndoBtn(container);
    btn.dataset.productId = pickedD;
    btn.setAttribute('data-product-id', pickedD);
    btn.dataset.parentProductId = thisItemVariantId;
    btn.setAttribute('data-parent-product-id', thisItemVariantId);
    btn.setAttribute('data-undo-product-event-id','swap-undo-cart-product-event');
  }

  function hideTargets() {
    document.querySelectorAll(TARGETS).forEach(el => {
      if (cfg.remove) el.parentNode && el.parentNode.removeChild(el);
      else { el.style.display='none'; el.setAttribute('hidden',''); el.classList.add('is-swap-hidden'); }
    });
  }

  function hideUndoButtons() {
    document.querySelectorAll(TARGETSu).forEach(el => {
      if (cfgu.remove) el.parentNode && el.parentNode.removeChild(el);
      else { el.style.display='none'; el.setAttribute('hidden',''); el.classList.add('is-swap-hidden'); }
    });
  }

// to hide the swap product
// function hideCartSwapButtons() {
//   document.querySelectorAll('.cart_button_dt_swap, .test_cdw')
//     .forEach(el => el.style.setProperty('display', 'none', 'important'));
// }

// cart item checking to show and hide swap item
//   function checkCartItems() {
//     fetch('/cart.js', {
//       headers: { 'Accept': 'application/json' },
//       cache: 'no-store'
//     })
//       .then(res => res.json())
//       .then(cart => {
//         if (cart.item_count > 1) {
//           // console.log("Cart has items:", cart.item_count);
//           hideCartSwapButtons();
//         } 
//       })
//       .catch(err => {
//         console.error("Error fetching cart:", err);
//       });
// }


  // --- run all actions; call test(reason) if present ---
  function act(reason) {
    
    // checkCartItems();
    if (hasKey())  hideTargets();
    if (hasKeyu()) hideUndoButtons();
    appendSwapUndoIfMatch();
    
    if (typeof window.applyWithDelay === 'function') requestAnimationFrame(() => window.applyWithDelay());
    if (typeof window.test === 'function') window.test(reason);
  }

  // --- throttle to 1/run per frame ---
  const schedule = (why) => {
    if (schedule._p) return; schedule._p = true;
    requestAnimationFrame(() => { schedule._p = false; act(why); });
  };

  // ===== NEW: Observe the CART DRAWER ROOT (always fires on open/update) =====
  let drawerMO = null;
  function getDrawerRoot(){
    return document.querySelector('cart-drawer') ||
           document.querySelector('#CartDrawer') ||
           document.querySelector('.drawer--cart');
  }
  function observeDrawerRoot(){
    const root = getDrawerRoot();
    if (!root) return false;
    if (drawerMO) drawerMO.disconnect();
    drawerMO = new MutationObserver(() => schedule('drawer:mutated'));
    // attributes: true catches open/close (class/aria/hidden changes), subtree catches internal updates
    drawerMO.observe(root, { attributes:true, attributeFilter:['open','aria-hidden','class','hidden','style'], childList:true, subtree:true });
    // run once when we first hook it (covers "open" right now)
    schedule('drawer:hooked');
    return true;
  }

  // events: open/render/update/refresh (if theme emits them)
  ['cart-drawer:open','cart-drawer:rendered','cart:updated','cart:refresh']
    .forEach(ev => document.addEventListener(ev, () => schedule(ev), { passive:true }));

  // watch DOM changes in light scopes (your watchers)
  const seen = new WeakSet();
  function attachScopedObservers(){
    WATCHERS.forEach(sel=>{
      document.querySelectorAll(sel).forEach(n=>{
        if (!n || seen.has(n)) return;
        seen.add(n);
        new MutationObserver(()=>schedule('dom:changed'))
          .observe(n,{childList:true,subtree:true,attributes:true});
      });
    });
  }

  // Re-attach when Shopify reloads sections
  document.addEventListener('shopify:section:load', () => {
    observeDrawerRoot();
    attachScopedObservers();
    schedule('section:load');
  }, { passive:true });

  // ===== Tiny retry to catch lazy-mounted drawer =====
  // Tries a few times after load to find the drawer root if it wasn’t in the DOM yet.
  function ensureDrawerSoon(tries=10){
    if (observeDrawerRoot()) return;
    if (tries <= 0) return;
    setTimeout(()=>ensureDrawerSoon(tries-1), 300);
  }

  // init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observeDrawerRoot();
      attachScopedObservers();
      ensureDrawerSoon();        // <-- important for drawers mounted after first paint
      schedule('init');
    }, { once:true });
  } else {
    observeDrawerRoot();
    attachScopedObservers();
    ensureDrawerSoon();
    schedule('init');
  }
})();
// if swap product available than it will remove the swap suggestion item from cart 

//**************************************************************************************************************//
